import { Check } from 'lucide-react';
import type { ReactNode } from 'react';

import { PreviewHeader } from '../PreviewHeader';
import { AadhaarCardMock } from './AadhaarCardMock';

const INSTRUCTIONS = [
  'Use the QR-code side of the Aadhaar — the big square QR (back of the PVC card, or the e-Aadhaar PDF). NOT the photo/front side.',
  'Point the camera at the QR only and fill the frame with it — a clear, close, sharp shot reads best.',
  'Keep it flat with good lighting — avoid glare, shadows and blur.',
  'No Aadhaar number is shared — only the details stored inside the QR.',
];

// Aadhaar Secure QR — scan / upload the UIDAI Secure QR printed on the Aadhaar
// card or e-Aadhaar letter. Synchronous: the QR is parsed on the server and the
// demographics are returned immediately. Same scan-frame styling as OCR; copy +
// framing target the QR square. `children` lets the session swap in the live
// captured image; `large` renders the session-sized placeholder card (builder
// phone preview uses the small default so the phone mock isn't distorted).
export function AadhaarSecureQrScreen({
  children,
  large = false,
}: {
  children?: ReactNode;
  large?: boolean;
}) {
  return (
    <>
      <PreviewHeader title="Aadhaar Verification" subtitle="Upload a photo of the Secure QR code" />

      <div className="relative mb-2 w-full overflow-hidden rounded-2xl border border-[#99B6FF] bg-[#E5EDFF] px-6 py-5">
        <div className="absolute left-3 top-3 z-20 h-4 w-4 border-l-[2.5px] border-t-[2.5px] border-[#0048FF]" />
        <div className="absolute right-3 top-3 z-20 h-4 w-4 border-r-[2.5px] border-t-[2.5px] border-[#0048FF]" />
        <div className="absolute bottom-2 left-3 z-20 h-4 w-4 border-b-[2.5px] border-l-[2.5px] border-[#0048FF]" />
        <div className="absolute bottom-2 right-3 z-20 h-4 w-4 border-b-[2.5px] border-r-[2.5px] border-[#0048FF]" />
        <div className="animate-scan-line absolute left-0 z-30 h-0.5 w-full bg-[#0048FF] shadow-[0_0_8px_2px_rgba(0,72,255,0.5)]" />
        {children ?? <AadhaarCardMock large={large} compact={!large} />}
      </div>

      <ul className="mb-2 flex flex-col gap-2.5">
        {INSTRUCTIONS.map((text) => (
          <li
            key={text}
            className="flex items-start gap-2 text-[length:var(--text-12)] leading-tight text-gray30"
          >
            <div className="flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full bg-primary text-white">
              <Check size={10} strokeWidth={4} />
            </div>
            <span>{text}</span>
          </li>
        ))}
      </ul>

      <p className="mb-3 mt-2 text-[11px] leading-snug text-[#6B7280]">
        Offline verification via the UIDAI Secure QR — your full Aadhaar number is never shared.
      </p>
    </>
  );
}
